// src/lib/wallet.ts
// ─────────────────────────────────────────────────────────────
// BILLETERA CON AUTORIDAD DE SERVIDOR.
//
// Reglas:
//  * Ningún módulo escribe `settings.flip_balance` directamente.
//  * Todo movimiento pasa por `post_wallet_entry` (RPC) y deja asiento en
//    `wallet_entries`.
//  * IndexedDB es caché: se actualiza con `balance_after` que devuelve el
//    servidor. Si el caché se manipula, el siguiente refresh lo corrige.
//  * Los abonos (recargas, bonos, ajustes) NO se hacen desde el cliente:
//    los autoriza el servidor (service_role / admin / webhook).
// ─────────────────────────────────────────────────────────────

import { db } from '@/db/db';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import type { PlanType, SubscriptionStatus } from '@/db/db';

export type WalletEntryType =
  | 'RECHARGE'
  | 'BONUS'
  | 'CHARGE'
  | 'SUBSCRIPTION'
  | 'REFUND'
  | 'ADJUSTMENT'
  | 'B2B_PAYMENT';

export interface WalletEntry {
  id: string;
  business_id: string;
  type: WalletEntryType;
  amount_coins: number;
  balance_after: number;
  reference_id: string | null;
  idempotency_key: string;
  sku: string | null;
  cop_amount: number | null;
  gateway_ref: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export type WalletErrorCode =
  | 'NO_CLOUD'
  | 'NO_SESSION'
  | 'INSUFFICIENT'
  | 'FORBIDDEN'
  | 'NOT_INITIALIZED'
  | 'NETWORK'
  | 'UNKNOWN';

export interface WalletResult {
  ok: boolean;
  balance: number;
  entry?: WalletEntry;
  error?: string;
  code?: WalletErrorCode;
}

interface RpcResponse {
  data: unknown;
  error: { message: string } | null;
}

interface RpcCapableClient {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<RpcResponse>;
}

const asRpc = (): RpcCapableClient | null =>
  supabase ? (supabase as unknown as RpcCapableClient) : null;

/** ¿Hay nube configurada y sesión de Supabase? */
export const hasWalletSession = async (): Promise<boolean> => {
  if (!supabase) return false;
  try {
    const { data } = await supabase.auth.getSession();
    return Boolean(data.session);
  } catch {
    return false;
  }
};

export const currentBusinessId = (): string =>
  String(useAuthStore.getState().user?.business_id || '').trim();

/** Clave de idempotencia estándar NIIZ. */
export const walletIdempotencyKey = (
  businessId: string,
  sku: string,
  referenceId: string,
): string => `${businessId}:${sku}:${referenceId}`;

const mapError = (message: string): WalletErrorCode => {
  const text = message.toUpperCase();
  if (text.includes('INSUFFICIENT_BALANCE')) return 'INSUFFICIENT';
  if (text.includes('WALLET_NOT_INITIALIZED')) return 'NOT_INITIALIZED';
  if (text.includes('FORBIDDEN')) return 'FORBIDDEN';
  if (text.includes('JWT') || text.includes('SESSION')) return 'NO_SESSION';
  return 'UNKNOWN';
};

const normalizeEntry = (raw: unknown): WalletEntry | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  if (typeof row['id'] !== 'string') return undefined;
  return {
    id: row['id'],
    business_id: String(row['business_id'] ?? ''),
    type: String(row['type'] ?? 'CHARGE') as WalletEntryType,
    amount_coins: Number(row['amount_coins'] ?? 0),
    balance_after: Number(row['balance_after'] ?? 0),
    reference_id: typeof row['reference_id'] === 'string' ? row['reference_id'] : null,
    idempotency_key: String(row['idempotency_key'] ?? ''),
    sku: typeof row['sku'] === 'string' ? row['sku'] : null,
    cop_amount: row['cop_amount'] === null || row['cop_amount'] === undefined ? null : Number(row['cop_amount']),
    gateway_ref: typeof row['gateway_ref'] === 'string' ? row['gateway_ref'] : null,
    metadata: (row['metadata'] as Record<string, unknown>) ?? {},
    created_at: String(row['created_at'] ?? new Date().toISOString()),
  };
};

/** Actualiza el caché local de saldo con el valor del servidor. */
const writeBalanceCache = async (businessId: string, balance: number): Promise<void> => {
  const row = await db.settings.where('business_id').equals(businessId).first();
  if (!row?.id) return;
  await db.settings.update(row.id, { flip_balance: balance });
};

export interface DebitInput {
  amount: number;
  sku: string;
  referenceId: string;
  businessId?: string;
  type?: 'CHARGE' | 'SUBSCRIPTION' | 'B2B_PAYMENT';
  metadata?: Record<string, unknown>;
  idempotencyKey?: string;
}

/**
 * Cobra monedas. Solo importes negativos; el servidor valida saldo y
 * pertenencia del negocio. Si no hay nube/sesión, FALLA (no se permite
 * crear deuda local).
 */
export const debitCoins = async (input: DebitInput): Promise<WalletResult> => {
  const rpc = asRpc();
  if (!rpc) return { ok: false, balance: 0, error: 'NIIZ Cloud no está configurado.', code: 'NO_CLOUD' };

  const businessId = String(input.businessId || currentBusinessId()).trim();
  if (!businessId || businessId === 'local') {
    return { ok: false, balance: 0, error: 'Inicia sesión en la nube para usar la billetera.', code: 'NO_SESSION' };
  }
  if (!Number.isFinite(input.amount) || input.amount <= 0) {
    return { ok: false, balance: 0, error: 'Importe inválido.', code: 'UNKNOWN' };
  }

  const key = input.idempotencyKey || walletIdempotencyKey(businessId, input.sku, input.referenceId);

  const { data, error } = await rpc.rpc('post_wallet_entry', {
    p_business_id: businessId,
    p_type: input.type || 'CHARGE',
    p_amount_coins: -Math.round(input.amount),
    p_reference_id: input.referenceId,
    p_idempotency_key: key,
    p_sku: input.sku,
    p_cop_amount: null,
    p_gateway_ref: null,
    p_metadata: input.metadata ?? {},
  });

  if (error) {
    const code = mapError(error.message);
    return {
      ok: false,
      balance: 0,
      error:
        code === 'INSUFFICIENT'
          ? 'Saldo insuficiente. Recarga tu billetera para continuar.'
          : code === 'NOT_INITIALIZED'
            ? 'La billetera no está inicializada en la nube. Sincroniza tu negocio.'
            : error.message,
      code,
    };
  }

  const entry = normalizeEntry(data);
  const balance = entry?.balance_after ?? 0;
  await writeBalanceCache(businessId, balance);
  return {
    ok: true,
    balance,
    ...(entry ? { entry } : {}),
  };
};

/**
 * El cliente NO reembolsa: solicita el reembolso. Un admin lo aprueba.
 * Los reembolsos automáticos solo los ejecuta el servidor cuando marcó
 * `metadata.service_failed = true`.
 */
export const requestRefund = async (
  entryId: string,
  reason = 'Fallo del servicio',
): Promise<{ ok: boolean; error?: string }> => {
  const rpc = asRpc();
  if (!rpc) return { ok: false, error: 'NIIZ Cloud no está configurado.' };
  const { error } = await rpc.rpc('request_refund', {
    p_entry_id: entryId,
    p_reason: reason,
  });
  return error ? { ok: false, error: error.message } : { ok: true };
};

/** @deprecated Usa `requestRefund`; el reembolso directo es solo del servidor. */
export const refundEntry = async (entryId: string): Promise<WalletResult> => {
  const result = await requestRefund(entryId);
  return result.ok
    ? { ok: true, balance: 0 }
    : { ok: false, balance: 0, code: 'FORBIDDEN', ...(result.error ? { error: result.error } : {}) };
};

/** Compra/renueva un plan con coins. El precio lo pone el servidor. */
export const purchasePlan = async (plan: PlanType, idempotencyKey: string): Promise<WalletResult> => {
  const rpc = asRpc();
  if (!rpc) return { ok: false, balance: 0, error: 'NIIZ Cloud no está configurado.', code: 'NO_CLOUD' };
  const { data, error } = await rpc.rpc('purchase_plan', {
    p_plan: plan,
    p_idempotency_key: idempotencyKey,
  });
  if (error) {
    return { ok: false, balance: 0, error: error.message, code: mapError(error.message) };
  }
  const entry = normalizeEntry(data);
  const businessId = currentBusinessId();
  // Si el asiento ya existía (misma clave), no se extiende el plan otra vez.
  const ageMs = entry ? Date.now() - new Date(entry.created_at).getTime() : Number.POSITIVE_INFINITY;
  if (!entry || ageMs > 120_000) {
    return {
      ok: false,
      balance: entry?.balance_after ?? 0,
      error: 'Esta compra ya fue procesada. Actualiza el estado para ver tu plan.',
      code: 'FORBIDDEN',
      ...(entry ? { entry } : {}),
    };
  }
  if (entry && businessId) {
    await writeBalanceCache(businessId, entry.balance_after);
  }
  await refreshPlanCache();
  return { ok: true, balance: entry.balance_after, entry };
};

/** Reclama el bono beta (el servidor valida dominio e idempotencia). */
export const claimBetaBonus = async (): Promise<WalletResult> => {
  const rpc = asRpc();
  if (!rpc) return { ok: false, balance: 0, error: 'La nube no está configurada.', code: 'NO_CLOUD' };
  const { data, error } = await rpc.rpc('claim_beta_bonus', {});
  if (error) return { ok: false, balance: 0, error: error.message, code: mapError(error.message) };
  const entry = normalizeEntry(data);
  const businessId = currentBusinessId();
  if (entry && businessId) await writeBalanceCache(businessId, entry.balance_after);
  return { ok: true, balance: entry?.balance_after ?? 0, ...(entry ? { entry } : {}) };
};

/** Abono manual solo para administradores de plataforma. */
export const adminCredit = async (input: {
  businessId: string;
  amount: number;
  type?: 'RECHARGE' | 'ADJUSTMENT' | 'BONUS';
  sku: string;
  referenceId: string;
  idempotencyKey: string;
  copAmount?: number;
  gatewayRef?: string;
  metadata?: Record<string, unknown>;
}): Promise<WalletResult> => {
  const rpc = asRpc();
  if (!rpc) return { ok: false, balance: 0, error: 'NIIZ Cloud no está configurado.', code: 'NO_CLOUD' };
  const { data, error } = await rpc.rpc('admin_credit_wallet', {
    p_business_id: input.businessId,
    p_type: input.type || 'RECHARGE',
    p_amount_coins: Math.round(input.amount),
    p_reference_id: input.referenceId,
    p_idempotency_key: input.idempotencyKey,
    p_sku: input.sku,
    p_cop_amount: input.copAmount ?? null,
    p_gateway_ref: input.gatewayRef ?? null,
    p_metadata: input.metadata ?? {},
  });
  if (error) return { ok: false, balance: 0, error: error.message, code: mapError(error.message) };
  const entry = normalizeEntry(data);
  return { ok: true, balance: entry?.balance_after ?? 0, ...(entry ? { entry } : {}) };
};

export interface WalletSnapshot {
  balance: number;
  planType: PlanType;
  subscriptionStatus: SubscriptionStatus;
  planExpiresAt: string | null;
  autoRenew: boolean;
  cloudUsageMb: number;
  cloudExtraGb: number;
  graceStartedAt: string | null;
}

/** Lee el estado autoritativo desde Supabase y actualiza el caché local. */
export const refreshWallet = async (): Promise<WalletSnapshot | null> => {
  if (!supabase) return null;
  const localBusinessId = currentBusinessId();
  if (!localBusinessId || localBusinessId === 'local') return null;

  let businessId = localBusinessId;
  let settingsRes = await supabase
    .from('settings')
    .select('*')
    .eq('business_id', businessId)
    .limit(1)
    .maybeSingle();

  // Si el business_id local está desactualizado, se resuelve por auth.uid()
  // con el RPC `current_business_id()` y se reintenta contra Supabase.
  if (settingsRes.error || !settingsRes.data) {
    const { data: cloudBusiness, error: businessError } = await (supabase as any).rpc('current_business_id');
    const resolved = String(cloudBusiness || '').trim();
    if (!businessError && resolved && resolved !== businessId) {
      console.warn(`[PLAN SYNC] business_id local "${businessId}" no coincide; usando "${resolved}"`);
      businessId = resolved;
      settingsRes = await supabase
        .from('settings')
        .select('*')
        .eq('business_id', businessId)
        .limit(1)
        .maybeSingle();
    }
  }

  if (settingsRes.error || !settingsRes.data) return null;
  const row = settingsRes.data as Record<string, unknown>;
  const { data: walletData } = await supabase
    .from('wallets')
    .select('balance')
    .eq('business_id', businessId)
    .maybeSingle();
  const walletRow = walletData as Record<string, unknown> | null;
  const snapshot: WalletSnapshot = {
    balance: Number(walletRow?.['balance'] ?? row['flip_balance'] ?? 0),
    planType: String(row['plan_type'] ?? 'FREE') as PlanType,
    subscriptionStatus: String(row['subscription_status'] ?? 'ACTIVE') as SubscriptionStatus,
    planExpiresAt: typeof row['plan_expires_at'] === 'string' ? row['plan_expires_at'] : null,
    autoRenew: row['auto_renew'] !== false,
    cloudUsageMb: Number(row['cloud_usage_mb'] ?? 0),
    cloudExtraGb: Number(row['cloud_extra_gb'] ?? 0),
    graceStartedAt: typeof row['grace_period_started_at'] === 'string' ? row['grace_period_started_at'] : null,
  };

  let local = await db.settings.where('business_id').equals(localBusinessId).first();
  if (!local) local = await db.settings.where('business_id').equals(businessId).first();
  if (local?.id) {
    await db.settings.update(local.id, {
      flip_balance: snapshot.balance,
      plan_type: snapshot.planType,
      subscription_status: snapshot.subscriptionStatus,
      auto_renew: snapshot.autoRenew,
      cloud_usage_mb: snapshot.cloudUsageMb,
      cloud_extra_gb: snapshot.cloudExtraGb,
      ...(snapshot.planExpiresAt ? { plan_expires_at: snapshot.planExpiresAt } : {}),
      ...(snapshot.graceStartedAt ? { grace_period_started_at: snapshot.graceStartedAt } : {}),
    });
    console.info(
      `[PLAN SYNC] ${businessId}: ${snapshot.planType} · ${snapshot.subscriptionStatus}` +
      (snapshot.planExpiresAt ? ` · vence ${snapshot.planExpiresAt.slice(0, 10)}` : ''),
    );
  }
  return snapshot;
};

/** Sincroniza solo el plan (tras comprarlo). */
export const refreshPlanCache = async (): Promise<void> => {
  await refreshWallet();
};

/** Movimientos del negocio, más recientes primero. */
export const listWalletEntries = async (limit = 50): Promise<WalletEntry[]> => {
  if (!supabase) return [];
  const businessId = currentBusinessId();
  if (!businessId) return [];
  const { data, error } = await supabase
    .from('wallet_entries')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return (data as unknown[]).map(normalizeEntry).filter((e): e is WalletEntry => Boolean(e));
};

/** ¿Hay un pase de feature vigente? El servidor valida SKU y monto. */
export const hasFeaturePass = async (sku: string, hours: number): Promise<boolean> => {
  if (!supabase) return false;
  const businessId = currentBusinessId();
  if (!businessId) return false;
  const { data, error } = await (supabase as any).rpc('has_feature_pass', {
    p_business_id: businessId,
    p_sku: sku,
    p_hours: hours,
  });
  if (error) return false;
  return Boolean(data);
};

/** Activa/desactiva la renovación automática del plan. */
export const setAutoRenew = async (enabled: boolean): Promise<boolean> => {
  const rpc = asRpc();
  if (!rpc) return false;
  const { error } = await rpc.rpc('set_auto_renew', { p_enabled: enabled });
  if (error) return false;
  const local = await db.settings.where('business_id').equals(currentBusinessId()).first();
  if (local?.id) await db.settings.update(local.id, { auto_renew: enabled });
  return true;
};

/** Solicita un abono que un admin debe aprobar (afiliados, etc.). */
export const requestWalletGrant = async (
  amountCoins: number,
  kind: string,
  reason: string,
): Promise<{ ok: boolean; error?: string }> => {
  const rpc = asRpc();
  if (!rpc) return { ok: false, error: 'NIIZ Cloud no está configurado.' };
  const { error } = await rpc.rpc('request_wallet_grant', {
    p_amount_coins: Math.round(amountCoins),
    p_kind: kind,
    p_reason: reason,
  });
  return error ? { ok: false, error: error.message } : { ok: true };
};

/**
 * Crea un link de pago de Wompi con referencia verificable.
 * Requiere la Edge Function `wompi-create-payment` desplegada.
 */
export const createPaymentLink = async (input: {
  kind: 'recharge' | 'b2b';
  pendingId?: string;
  copAmount?: number;
  plan?: string;
}): Promise<{ ok: boolean; url?: string; error?: string }> => {
  if (!supabase) return { ok: false, error: 'NIIZ Cloud no está configurado.' };
  try {
    const { data, error } = await (supabase as any).functions.invoke('wompi-create-payment', {
      body: input,
    });
    if (error || !data?.url) {
      return { ok: false, error: error?.message || 'Wompi no devolvió un link' };
    }
    return { ok: true, url: String(data.url) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'No se pudo crear el link' };
  }
};
